import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardFooter } from "@/components/ui/card";
import { 
  MailIcon, 
  Copy, 
  Check, 
  AlertCircle, 
  Info, 
  Sparkles,
  CopyCheck,
  Search,
  UserCircle,
  MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { motion } from "framer-motion";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useParams } from "react-router-dom";
import { cn, copyFormattedContent } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";

// Email template interface
interface EmailTemplate {
  id: string;
  title: string;
  subject: string;
  body: string;
  category: "cold" | "followup" | "nurture";
  tags: string[];
}

const ColdOutreachPage = () => {
  const { id: projectId } = useParams();
  const { profile } = useProfile();
  const { toast } = useToast();
  
  // States
  const [projectName, setProjectName] = useState<string>("Your Product");
  const [isB2B, setIsB2B] = useState<boolean>(true); // Default to B2B
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [copied, setCopied] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("cold");
  
  // Check if user is B2B with timeout protection
  useEffect(() => {
    const checkUserType = async () => {
      const timeout = setTimeout(() => {
        console.warn("Supabase request timed out, using default settings");
      }, 5000); // 5 second timeout
      
      try {
        if (projectId) {
          const { data, error } = await supabase
            .from("projects")
            .select("name, company_type")
            .eq("id", projectId)
            .single();
          
          clearTimeout(timeout);
          
          if (error) {
            console.error("Error fetching project details:", error);
            return;
          }
          
          if (data) {
            const safeProjectName = data.name || "Your Product";
            setProjectName(safeProjectName);
            const isUserB2B = data.company_type !== "B2C";
            setIsB2B(isUserB2B);
          }
        }
      } catch (error) {
        clearTimeout(timeout);
        console.error("Error checking user type:", error);
      }
    };
    
    checkUserType();
  }, [projectId]);
  
  // All email templates
  const b2bEmailTemplates: EmailTemplate[] = [
    {
      id: "viral-tips",
      title: "Quick Viral Tips",
      subject: "Quick viral tips for [name]",
      body: `Hey [first_name] - I'm trying to make it a habit to send 12 cold email a day to people. Hope you're the one person who sees this :)

Do you [insert problem]? If so, send me a link to it so I can research it and tell you exactly how to [desired outcome].

You've seen how [Insert competitor] is growing, fixing [insert problem] is a priority.

Send the link, I'll do the rest.

PS: not a rookie. been doing this for 3 years, [Insert credentials], just a rookie with the cold emails haha...

Cheers,
[name]
[position]
[product]`,
      category: "cold",
      tags: ["casual", "viral", "marketing"]
    },
    {
      id: "problem-solver",
      title: "Problem Solver",
      subject: "Cold email about [problem]",
      body: `Hey [first_name],

You probably don't like cold emails, even when they come from companies that [insert accomplishment].

But sometimes, you've got to be bold to do business, right? So I'll cut to the chase:

Do you have [insert problem]?

We have helped [insert social proof] achieve [desired outcome] in [time frame] by [insert easy solution].

If this clicks with you, would it be crazy if I sent over a short video to show you how it works?

Just reply "yes" and I'll send it over. No strings attached.

Best,
[name]
[position]
[product]

PS: One of our new clients/users recently (from a cold email too btw), made/did [insert achievement]`,
      category: "cold",
      tags: ["professional", "problem-solution", "social proof"]
    },
    {
      id: "no-nigerian-prince",
      title: "Not a Nigerian Prince",
      subject: "Cold email about [problem] (not from a locked up Nigerian prince)",
      body: `Hey [first_name],

Yup, this is a cold email, but I'm not a nigerian prince trying to wire you millions, sorry to disappoint.

I noticed you are in [industry] and we have noticed many struggle with [insert problem].

We have helped [insert social proof] fix that and hit [desired outcome] in [time frame] by [insert easy solution].

If this clicks with you, would it be crazy if I sent over a explainer to show you how it works?

Just reply "yes" and I'll send it over. No strings attached.

Best,
[name]
[position]
[product]

P.S. One of our new clients/users recently (from a cold email too btw), made/did [insert achievement]`,
      category: "cold",
      tags: ["humor", "problem-solution", "industry-specific"]
    },
    {
      id: "result-focused",
      title: "Result Focused",
      subject: "Quick Question About [result_they_want]…",
      body: `Hi [first_name],

I hope you don't mind me reaching out, but I noticed something interesting about [their_business_situation].

You see most [industry_professionals] struggle with [specific_challenge], yet there's a surprisingly simple way to [specific_benefit] without [pain_point].

I've used it to help others achieve [specific_result], often faster and easier than they expected.

Would it be crazy if I sent over a short video to show you how it works?

No strings, just some value you can use right away.

Just reply "yes" and I'll send it over.

Cheers,
[name]
[position]
[product]

P.S. Even if you don't use [product], the video might spark a quick idea you can apply immediately. Zero obligation, just helpful insight.`,
      category: "cold",
      tags: ["results", "benefit-focused", "no obligation"]
    },
    {
      id: "reminded-of-you",
      title: "This Reminded Me of You",
      subject: "This reminded me of you",
      body: `Hey [first_name],

Yesterday, I was chatting with a client who used to spend hours wrestling with [specific_problem]. They tried [common_solution] and even [another_effort], but nothing seemed to stick.

Then we made one small tweak.

And suddenly, [specific_outcome] happened. Almost like flipping a switch.

The reason I thought of you is because I noticed [specific_situation]. Odds are, the same approach could work wonders for you too.

I'd be happy to walk you through exactly what we did. No charge, no catch.

Want me to send over a quick breakdown?

Just reply "yes" and I'll send it over. No strings attached.

Cheers,
[name]
[position]
[product]

P.S. You'll probably spot at least one thing you can apply right away — even if we don't end up working together. Worth a quick peek.`,
      category: "cold",
      tags: ["storytelling", "personalized", "no-catch"]
    },
    {
      id: "underusing-saas",
      title: "You're Underusing This",
      subject: "You're probably under-using this (but it's not your fault)",
      body: `Hey [first_name],

Quick question:

A lot of [role/industry] teams I speak with are sitting on a SaaS tool that could streamline [specific task or process] and boost [benefit like productivity/revenue/leads] — but they're only using 40% of its potential.

It's usually because no one pointed out a tiny feature or workflow that changes everything.

We helped [Client/Industry Example] unlock it and saw [specific result: 25% faster onboarding, 2X retention, etc.].

Want me to send over a quick explainer?

Could be something your team can implement in 10 minutes.

Just reply "yes" and I'll send it over. No strings attached.

Cheers,
[name]
[position]
[product]

P.S. Even if you decide it's not a fit, you'll walk away knowing one small tweak that could immediately boost [desired outcome]. No strings attached.`,
      category: "cold",
      tags: ["optimization", "quick-win", "industry-specific"]
    },
    {
      id: "direct-help",
      title: "Direct Help Offer",
      subject: "This will help you [desired outcome]",
      body: `Hey [first_name],

We don't know each other, but I do know one thing:

If you're still [manual process/problem], you're wasting hours every week.

${projectName} fixes that.

It handles [specific result] [insert ease of use] [insert time saved]

Want me to send over a quick explainer?

Just reply "yes" and I'll send it over.

Cheers,
[name]
[position]
[product]

P.S. Even if you don't move forward, you'll still get a simple trick to cut [time/cost/friction] starting today. Worth a quick look.`,
      category: "cold",
      tags: ["direct", "time-saving", "explainer"]
    },
    {
      id: "time-saver",
      title: "Time Saver Case Study",
      subject: "How [Company] saved 10 hours a week (and didn't hire anyone new)",
      body: `Hi [first_name],

Here's something interesting:

[Company/Industry Peer] was drowning in [specific problem: manual reports, onboarding chaos, etc.].

Instead of hiring extra staff or patching together more apps..

They used ${projectName} and automated the whole process.

The result?

10 hours a week saved. No extra hires.

[Specific ROI/result].

Want me to send over a quick explainer?

Just reply "yes" and I'll send it over. No strings. 

Cheers,
[name]
[position]
[product]

P.S. Even if you don't end up using ${projectName}, you'll walk away with a few simple tweaks you can implement right away to save time.`,
      category: "cold",
      tags: ["case-study", "time-saving", "ROI"]
    },
    {
      id: "time-money-waster",
      title: "Time/Money Waster",
      subject: "You're leaving [time/money] on the table",
      body: `Hey [first_name],

Quick and direct:

If you're still dealing with [problem], you're wasting [time/money] every single day.

${projectName} fixes that. Automates [process], eliminates [pain], and frees up [benefit].

Want me to send over a quick explainer?

Just reply "yes" and I'll send it over. No strings attached. 

Cheers,
[name]
[position]
[product]

P.S. Every day you delay is costing you. Fix it now, or keep paying for it later.`,
      category: "cold",
      tags: ["urgent", "cost-saving", "automation"]
    },
    {
      id: "free-offer",
      title: "Free Value Offer",
      subject: "Want a free [output of tool]? Celebrating a milestone!",
      body: `Hey [first_name],

Would you benefit from [output of tool]?

To celebrate a big milestone with ${projectName}, I'm offering to do [specific valuable task/result] for your SaaS.

Free. No strings.

Just send me [simple info they need to provide], and I'll run it through our process. The same one top teams/amount of users use to [benefit like rank, convert, optimize, etc.].

You'll walk away with something done for you, not added to your to-do list.

Let me know!

Cheers,
[name]
[position]
[product]

P.S. No deadline, but I'm only offering this while we're celebrating. Might as well grab it now.`,
      category: "cold",
      tags: ["free-offer", "limited-time", "celebration"]
    }
  ];

  // B2C email templates
  const b2cEmailTemplates: EmailTemplate[] = [
    {
      id: "quick-trick",
      title: "Quick Problem Solver",
      subject: "Quick trick to [solve_problem] in seconds",
      body: `Hey [first_name],

Ever struggle with [common_issue]? You're not alone.

We've helped thousands of people [achieve_desired_outcome] with just one easy trick.

Want to see how it's done? Reply "yes," and I'll send a quick explainer—totally free, no strings.

Cheers,

[name]
[position]
[product]

P.S. Takes less than a minute—instant relief guaranteed!`,
      category: "cold",
      tags: ["quick-fix", "problem-solver", "instant-relief"]
    },
    {
      id: "stop-activity",
      title: "Stop Undesirable Activity",
      subject: "Stop [undesirable_activity] right now",
      body: `Hi [first_name],

Still dealing with [undesirable_activity]? There's a simpler way.

I've got a proven method to help you [desired_outcome] in just [short_timeframe]. Interested?

Reply "yes" and I'll show you exactly how it works.

Cheers,

[name]
[position]
[product]

P.S. Totally free tip—no catch, just results!`,
      category: "cold",
      tags: ["proven-method", "time-saver", "solution"]
    },
    {
      id: "why-stuck",
      title: "Why You're Stuck",
      subject: "Here's why you're stuck with [common_frustration]",
      body: `Hey [first_name],

If you're tired of [common_frustration], you're not alone.

We've discovered an easy fix that has helped [social_proof] finally [desired_outcome] without any hassle.

Curious? Reply "yes," and I'll share the secret with you right away.

Best,

[name]
[position]
[product]

P.S. Quick, simple, and effective—promise.`,
      category: "cold",
      tags: ["frustration", "easy-fix", "social-proof"]
    },
    {
      id: "do-this-stop",
      title: "Do This, Stop Problem",
      subject: "Do this, stop [problem] today",
      body: `Hi [first_name],

What if you could instantly eliminate [problem]?

[product] is the easy way to [desired_result] without [pain_point]. Want to see how it works?

Just reply "yes," and I'll send you a super quick demo.

Cheers,

[name]
[position]
[product]

P.S. Even if you don't use it, you'll still learn something super valuable!`,
      category: "cold",
      tags: ["instant-solution", "demo-offer", "pain-point"]
    },
    {
      id: "missing-detail",
      title: "Missing Detail",
      subject: "You're probably missing this (it's life-changing)",
      body: `Hey [first_name],

Most people dealing with [specific_issue] miss one tiny detail that changes everything.

We've helped [number_of_people] solve [specific_issue] by doing just this one simple thing.

Want me to quickly show you? Reply "yes," and I'll send it your way.

Cheers,

[name]
[position]
[product]

P.S. It'll take seconds, but it could change your day.`,
      category: "cold",
      tags: ["life-changing", "simple-fix", "quick-tip"]
    },
    {
      id: "quick-way",
      title: "Quick Way to Achieve",
      subject: "Quick way to [achieve_desired_outcome]",
      body: `Hi [first_name],

Imagine never worrying about [specific_problem] again.

There's a surprisingly easy method we've shared with thousands who now enjoy [positive_outcome].

Want to know the secret? Just reply "yes."

Cheers,

[name]
[position]
[product]

P.S. No pressure—just a quick tip that works!`,
      category: "cold",
      tags: ["worry-free", "easy-method", "secret-tip"]
    },
    {
      id: "wasting-resources",
      title: "You're Wasting Resources",
      subject: "You're wasting [resource_type] on [problem]",
      body: `Hey [first_name],

Still dealing with [problem]? You're losing more than you think.

I can show you an easy fix that'll save you [specific_saving] instantly.

Interested? Reply "yes" and I'll send the details.

Cheers,

[name]
[position]
[product]

P.S. Easy fix, big impact—check it out!`,
      category: "cold",
      tags: ["resource-saving", "instant-results", "easy-fix"]
    },
    {
      id: "try-this-trick",
      title: "Try This Trick",
      subject: "Try this trick to instantly improve [desired_result]",
      body: `Hi [first_name],

Here's a super quick way to dramatically improve your [desired_result].

We've helped [number_of_users] experience immediate results.

Want to know how? Reply "yes" and I'll quickly explain.

Best,

[name]
[position]
[product]

P.S. It's free, fast, and effective—no strings.`,
      category: "cold",
      tags: ["quick-improvement", "immediate-results", "dramatic-change"]
    },
    {
      id: "fix-frustration",
      title: "Fix Your Frustration",
      subject: "This could fix your [specific_frustration] today",
      body: `Hey [first_name],

Annoyed by [specific_frustration]? There's a quick fix.

I've helped thousands of others eliminate it instantly. Want the simple steps?

Reply "yes" and I'll send it right over.

Cheers,

[name]
[position]
[product]

P.S. Simple solution—big relief!`,
      category: "cold",
      tags: ["quick-fix", "frustration", "simple-steps"]
    },
    {
      id: "celebration-gift",
      title: "Celebration Gift",
      subject: "Celebrating with a gift for you",
      body: `Hi [first_name],

We're celebrating a milestone with [product] and giving away [valuable_offer].

Want yours?

Just reply "yes"—no strings attached.

Cheers,

[name]
[position]
[product]

P.S. Limited offer—grab yours before they're gone!`,
      category: "cold",
      tags: ["gift", "limited-offer", "celebration"]
    }
  ];
  
  // Filter templates based on search and active tab, using appropriate template set
  const filteredTemplates = (isB2B ? b2bEmailTemplates : b2cEmailTemplates).filter(template => {
    const matchesSearch = searchQuery === "" || 
      template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = template.category === activeTab;
    
    return matchesSearch && matchesCategory;
  });
  
  // Handle template selection
  const handleSelectTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(template);
  };
  
  // Handle copy to clipboard
  const handleCopyToClipboard = () => {
    if (!selectedTemplate) {
      toast({
        variant: "destructive",
        title: "No template selected",
        description: "Please select a template first",
      });
      return;
    }
    
    // Format the content with markdown-style formatting for better presentation
    const formattedContent = `**Subject:** ${selectedTemplate.subject}\n\n${selectedTemplate.body}`;
    
    copyFormattedContent(formattedContent, {
      onSuccess: () => {
        setCopied(true);
        toast({
          title: "Copied!",
          description: "Email template copied to clipboard",
        });
        setTimeout(() => setCopied(false), 2000);
      },
      onError: () => {
        toast({
          variant: "destructive",
          title: "Failed to copy",
          description: "Could not copy content to clipboard",
        });
      }
    });
  };
  
  // Calculate template stats
  const templateCount = filteredTemplates.length;
  const totalTemplates = isB2B ? b2bEmailTemplates.length : b2cEmailTemplates.length;
  
  return (
    <div className="container mx-auto py-6 px-4 md:px-6">
      <Card className="border-blue-500/20 shadow-lg overflow-hidden bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-800/80">
        <CardHeader className="pb-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-br from-zinc-50 to-white dark:from-zinc-900 dark:to-zinc-800">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-r from-blue-600 to-sky-500 shadow-md">
              <MailIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold gradient-text">
                Cold Outreach
              </h1>
              <p className="text-zinc-600 dark:text-zinc-400">
                Master the art of outreach{projectName ? ` for ${projectName}` : ""}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {!isB2B && (
            <Alert className="mb-6 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 shadow-sm">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-700 dark:text-blue-300">
                These templates are optimized for B2C products. Focus on consumer pain points and quick wins.
              </AlertDescription>
            </Alert>
          )}
          
          {/* Template stats pill */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-full py-2 px-4 flex items-center gap-3 shadow-md mb-6"
          >
            <div className="flex items-center gap-2">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Available templates
              </div>
              <Badge className="bg-blue-500 text-white border-blue-600 hover:bg-blue-600 dark:bg-blue-600 dark:border-blue-700 dark:text-blue-50">
                {templateCount}/{totalTemplates}
              </Badge>
            </div>
            <div className="h-4 w-px bg-gray-200 dark:bg-gray-700"></div>
            <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {searchQuery ? "Filtered results" : "All templates"}
            </div>
            {searchQuery && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700"
                onClick={() => setSearchQuery("")}
              >
                Clear
              </Button>
            )}
          </motion.div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Email Template Library - 4 columns */}
            <div className="lg:col-span-4 space-y-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <div className="relative w-full">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search templates..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 shadow-sm border-blue-200 dark:border-blue-800/30 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <Tabs defaultValue="cold" value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="w-full justify-start mb-2 bg-blue-100 dark:bg-blue-900/20 p-1 rounded-lg">
                    <TabsTrigger 
                      value="cold" 
                      className="flex items-center gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 data-[state=active]:shadow-sm rounded-md"
                    >
                      <MailIcon className="h-4 w-4" />
                      Cold Emails
                    </TabsTrigger>
                    {/* Only show these tabs for B2B as they're coming soon anyway */}
                    {isB2B === true && (
                      <>
                        <TabsTrigger 
                          value="followup" 
                          className="flex items-center gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 data-[state=active]:shadow-sm rounded-md" 
                          disabled
                        >
                          <CopyCheck className="h-4 w-4" />
                          Follow-ups
                        </TabsTrigger>
                        <TabsTrigger 
                          value="nurture" 
                          className="flex items-center gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 data-[state=active]:shadow-sm rounded-md" 
                          disabled
                        >
                          <UserCircle className="h-4 w-4" />
                          Nurture
                        </TabsTrigger>
                      </>
                    )}
                  </TabsList>
                  
                  {/* Content sections for tabs */}
                  <TabsContent value="cold" className="mt-0">
                    <ScrollArea className="h-[600px] pr-4">
                      <div className="space-y-3">
                        {filteredTemplates.length > 0 ? (
                          filteredTemplates.map((template) => (
                            <motion.div
                              key={template.id}
                              whileHover={{ scale: 1.01 }}
                              whileTap={{ scale: 0.99 }}
                              className={cn(
                                "cursor-pointer rounded-lg border transition-all shadow-sm",
                                selectedTemplate?.id === template.id 
                                  ? "bg-gradient-to-r from-blue-50 to-violet-50 dark:from-blue-900/10 dark:to-violet-900/10 border-blue-200 dark:border-blue-700" 
                                  : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-600 hover:bg-zinc-50 dark:hover:bg-gray-750"
                              )}
                              onClick={() => handleSelectTemplate(template)}
                            >
                              <div className="p-4">
                                <h3 className="font-medium mb-1 text-gray-900 dark:text-gray-100">{template.title}</h3>
                                <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-2 line-clamp-2">{template.subject}</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {template.tags.map((tag) => (
                                    <Badge 
                                      key={tag} 
                                      variant="outline" 
                                      className="text-xs bg-zinc-50 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700"
                                    >
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                              {selectedTemplate?.id === template.id && (
                                <div className="h-1 w-full bg-gradient-to-r from-blue-500 to-violet-500"></div>
                              )}
                            </motion.div>
                          ))
                        ) : (
                          <div className="text-center py-8">
                            <AlertCircle className="h-12 w-12 mx-auto text-blue-300 dark:text-blue-700 mb-2" />
                            <p className="text-muted-foreground font-medium">
                              {searchQuery 
                                ? "No templates match your search criteria" 
                                : "No templates available for this category"}
                            </p>
                            {searchQuery && (
                              <Button
                                variant="link"
                                onClick={() => setSearchQuery("")}
                                className="mt-2 text-blue-500 dark:text-blue-400"
                              >
                                Clear search
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </TabsContent>
                  
                  {/* Only render these tab contents if B2B since they're not relevant to B2C */}
                  {isB2B === true && (
                    <>
                      <TabsContent value="followup" className="mt-0">
                        <div className="text-center py-8 bg-gradient-to-b from-blue-50/50 to-white dark:from-blue-900/10 dark:to-gray-800 rounded-lg border border-blue-100 dark:border-blue-800/30 p-8">
                          <div className="mx-auto bg-gradient-to-br from-blue-100 to-violet-100 dark:from-blue-800/20 dark:to-violet-800/20 rounded-full h-16 w-16 flex items-center justify-center mb-4 shadow-sm">
                            <Sparkles className="h-8 w-8 text-blue-500 dark:text-blue-400" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Coming Soon</h3>
                          <p className="text-muted-foreground max-w-sm mx-auto">
                            Follow-up email templates will be available in our next update.
                          </p>
                          <div className="mt-6">
                            <Progress 
                              value={75} 
                              className="h-2 w-40 mx-auto rounded-full bg-gray-100 dark:bg-gray-700"
                              indicatorClassName="bg-gradient-to-r from-blue-400 to-violet-500 rounded-full"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">75% complete</p>
                          </div>
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="nurture" className="mt-0">
                        <div className="text-center py-8 bg-gradient-to-b from-blue-50/50 to-white dark:from-blue-900/10 dark:to-gray-800 rounded-lg border border-blue-100 dark:border-blue-800/30 p-8">
                          <div className="mx-auto bg-gradient-to-br from-blue-100 to-violet-100 dark:from-blue-800/20 dark:to-violet-800/20 rounded-full h-16 w-16 flex items-center justify-center mb-4 shadow-sm">
                            <Sparkles className="h-8 w-8 text-blue-500 dark:text-blue-400" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Coming Soon</h3>
                          <p className="text-muted-foreground max-w-sm mx-auto">
                            Nurture email sequences will be available in our next update.
                          </p>
                          <div className="mt-6">
                            <Progress 
                              value={50} 
                              className="h-2 w-40 mx-auto rounded-full bg-gray-100 dark:bg-gray-700"
                              indicatorClassName="bg-gradient-to-r from-blue-400 to-violet-500 rounded-full"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">50% complete</p>
                          </div>
                        </div>
                      </TabsContent>
                    </>
                  )}
                </Tabs>
              </div>
            </div>
            
            {/* Email Preview - 8 columns */}
            <div className="lg:col-span-8 space-y-6">
              {selectedTemplate ? (
                <Card className="border-blue-200 dark:border-blue-800/30 shadow-md overflow-hidden">
                  <CardHeader className="pb-3 flex flex-row items-center justify-between bg-gradient-to-r from-blue-50 to-violet-50 dark:from-blue-900/10 dark:to-violet-900/10 border-b border-blue-100 dark:border-blue-800/20">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{selectedTemplate.title}</h2>
                      <p className="text-sm text-muted-foreground">
                        Copy this template and personalize it in your email client
                      </p>
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            onClick={handleCopyToClipboard}
                            variant="default"
                            size="sm"
                            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm"
                          >
                            {copied ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                            {copied ? "Copied" : "Copy Template"}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Copy template to clipboard</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <Label className="text-sm font-medium text-blue-600 dark:text-blue-400 flex items-center gap-2">
                          <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400"></div>
                          </div>
                          SUBJECT LINE
                        </Label>
                        <div 
                          className="rounded-md border border-blue-200 dark:border-blue-800/30 px-4 py-3 bg-white dark:bg-gray-800/50 shadow-sm"
                          dangerouslySetInnerHTML={{ 
                            __html: selectedTemplate.subject.replace(
                              /\[(.*?)\]/g, 
                              match => `<span class="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 rounded-md font-medium">${match}</span>`
                            )
                          }}
                        />
                      </div>
                      
                      <div className="space-y-1">
                        <Label className="text-sm font-medium text-blue-600 dark:text-blue-400 flex items-center gap-2">
                          <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400"></div>
                          </div>
                          EMAIL BODY
                        </Label>
                        <div className="rounded-md border border-blue-200 dark:border-blue-800/30 p-5 bg-white dark:bg-gray-800/50 shadow-sm min-h-[300px] whitespace-pre-line">
                          {selectedTemplate.body.split('\n').map((line, i) => {
                            // Only highlight variables that haven't been filled out (those in brackets)
                            const highlightedLine = line.replace(/\[(.*?)\]/g, 
                              (match) => `<span class="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 rounded-md font-medium">${match}</span>`
                            );
                            
                            return (
                              <div 
                                key={i} 
                                className="mb-3"
                                dangerouslySetInnerHTML={{ __html: highlightedLine }}
                              />
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="border-t bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/10 dark:to-yellow-900/10 py-4 px-6">
                    <Alert className="bg-amber-100 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/30 shadow-sm">
                      <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      <AlertDescription className="text-amber-700 dark:text-amber-300">
                        Replace the highlighted text in brackets with your own details when sending.
                      </AlertDescription>
                    </Alert>
                  </CardFooter>
                </Card>
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-12 text-center">
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-blue-500/10 blur-3xl"></div>
                    <div className="relative space-y-5 bg-white/80 dark:bg-gray-800/80 rounded-xl p-8 backdrop-blur-sm border border-blue-100 dark:border-blue-800/30 shadow-lg">
                      <div className="mx-auto bg-gradient-to-br from-blue-100 to-violet-100 dark:from-blue-800/20 dark:to-violet-800/20 rounded-full h-20 w-20 flex items-center justify-center mb-4 shadow-sm">
                        <MailIcon className="h-10 w-10 text-blue-500 dark:text-blue-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Select a Template</h2>
                      <p className="text-muted-foreground max-w-md">
                        Choose an email template from the library to get started. You can copy it and customize in your email client.
                      </p>
                      <div className="pt-2">
                        <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800/30">
                          {templateCount} templates available
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ColdOutreachPage; 